import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, isRef as _isRef, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col h-full" }
const _hoisted_2 = { class: "flex items-center justify-center" }
const _hoisted_3 = { class: "flex grow justify-center w-full font-sourcesanspro-semibold text-l2" }
const _hoisted_4 = {
  key: 0,
  class: "flex grow flex-col items-center justify-center px-[34px]"
}
const _hoisted_5 = {
  key: 1,
  class: "flex grow flex-col justify-center px-[34px]"
}
const _hoisted_6 = { class: "flex flex-col items-center text-center rounded-xl bg-white/15 backdrop-blur p-6 text-white" }
const _hoisted_7 = { class: "font-sourcesanspro-semibold text-l2 mb-3" }
const _hoisted_8 = { class: "font-sourcesanspro-regular text-base leading-6" }
const _hoisted_9 = {
  key: 2,
  class: "flex grow flex-col justify-center px-[34px]"
}
const _hoisted_10 = { class: "mb-4 font-sourcesanspro-regular text-center text-sm2 font-color-text-50" }
const _hoisted_11 = { class: "flex flex-wrap w-full justify-left item-center p-2 rounded-full background-white-15 mb-8" }
const _hoisted_12 = { class: "flex w-full items-center px-6" }
const _hoisted_13 = { class: "separator mb-8" }
const _hoisted_14 = { class: "font-sourcesanspro-regular text-base text-white/50" }
const _hoisted_15 = {
  key: 3,
  class: "flex flex-col w-full px-[34px] align-center mb-8"
}
const _hoisted_16 = { class: "flex w-full items-center justify-between" }
const _hoisted_17 = {
  key: 4,
  class: "flex flex-col w-full px-[34px] align-center mb-8"
}
const _hoisted_18 = { class: "flex w-full items-center justify-between" }

import { onMounted, ref, watch } from 'vue';
import router from '@/router';
import { useRoute } from 'vue-router';
import { IonButton, IonContent, IonIcon, IonInput, IonSpinner } from '@ionic/vue';
import { alertCircleOutline, arrowForwardOutline, chevronBackOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';

import { TransactionType } from '@/enums/wallet.enum';
import { useTransactionStore } from '@/stores/transaction/transactionStore';
import hexRgb from 'hex-rgb';
import rgbHex from 'rgb-hex';
import ScanQR from '@/shared/components/ScanQR.vue';
import TopBar from '@/shared/components/TopBar.vue';
import type { FreeTickets, VoucherErrorMessages } from '@/shared/interfaces/voucher';
import type { Transaction } from '@/shared/interfaces/transaction';
import useToast from '@/shared/composables/useToast';
import useUtils from '@/shared/composables/useUtils';
import useVoucher from '@/modules/vouchers/composables/useVoucher';

// Init

export default /*@__PURE__*/_defineComponent({
  __name: 'RedeemVoucherPage',
  setup(__props) {

const { t, locale } = useI18n();
const route = useRoute();
const transactionStore = useTransactionStore();
const { setItemsAndTotal } = transactionStore;
const { errorVoucher, redeemVoucher, voucherText, voucherData, isLoadingVoucher } = useVoucher();
const { presentToast } = useToast();
const { calculateShade, convertToCurrency } = useUtils();
const primary_color = window.primary_color;
const primaryRgb = hexRgb(window.primary_color, { format: 'array' });
const primaryShade = '#' + rgbHex(calculateShade(primaryRgb.toString(), 0.2));
const autoSubmittedVoucherID = ref<string | null>(null);
const currentVoucherRedeemOrigin = ref<'qr' | 'manual' | null>(null);
const qrErrorMessage = ref<string | null>(null);

const voucherIDFromQuery = () => {
	const voucherID = route.query.voucherID;

	return Array.isArray(voucherID) ? voucherID[0] : voucherID;
};

const submitVoucherIDFromQuery = () => {
	const voucherID = voucherIDFromQuery();

	if (!voucherID || autoSubmittedVoucherID.value === voucherID) {
		return;
	}

	voucherText.value = voucherID;
	autoSubmittedVoucherID.value = voucherID;
	currentVoucherRedeemOrigin.value = 'qr';
	qrErrorMessage.value = null;
	redeemVoucher(voucherID);
};

const walletBalanceAmount = (item: FreeTickets) => {
	return Number(item.amount ?? item.item_extra_data?.amount ?? 0);
};

const walletBalanceTotalString = (items: FreeTickets[]) => {
	const totals = items.reduce<Record<string, number>>((acc, item) => {
		const currency = item.currency?.prefix ?? '€';
		acc[currency] = (acc[currency] ?? 0) + walletBalanceAmount(item);

		return acc;
	}, {});

	return Object.entries(totals)
		.map(([currency, amount]) => `${currency} ${convertToCurrency(amount)}`)
		.join(' + ');
};

const resolveVoucherErrorMessage = (errorKey: string, errorMessages?: VoucherErrorMessages) => {
	const activeLocale = String(locale.value ?? 'en').split('-')[0];

	return errorMessages?.[activeLocale] || errorMessages?.en || t(errorKey);
};

const resolveVoucherSuccessMessage = (successMessages?: VoucherErrorMessages) => {
	const activeLocale = String(locale.value ?? 'en').split('-')[0];
	const message = successMessages?.[activeLocale];

	return typeof message === 'string' && message.trim() ? message : '';
};

const redeemVoucherManually = () => {
	currentVoucherRedeemOrigin.value = 'manual';
	qrErrorMessage.value = null;
	redeemVoucher(voucherText.value);
};

onMounted(() => {
	submitVoucherIDFromQuery();
});

watch(() => route.query.voucherID, () => {
	submitVoucherIDFromQuery();
});

// Watch any change on voucherData
watch(voucherData, async () => {
	if (voucherData.value && voucherData.value.error) {
		if (currentVoucherRedeemOrigin.value === 'qr') {
			qrErrorMessage.value = resolveVoucherErrorMessage(voucherData.value.error, voucherData.value.errorMessages);

			return;
		}

		await presentToast('top', t(voucherData.value.error), 5000, 'danger');
	} else if (voucherData.value?.voucherMetaData && (voucherData.value.voucherMetaData.status === 'redeemed' || voucherData.value.voucherMetaData.status === 'partially_redeemed') && voucherData.value.voucherRedeemedItems.length) {
		const walletBalanceAdded: FreeTickets[] = voucherData.value.voucherRedeemedItems.filter((item: FreeTickets) => item.item_type === 'wallet_balance');
		const walletBalanceTotal = walletBalanceAdded.reduce((total, item) => total + walletBalanceAmount(item), 0);
		const sharedClaimSuccessMessage = resolveVoucherSuccessMessage(voucherData.value.voucherMetaData.shared_claim?.success_messages);

		const voucherItems: any = [];
		// Transform in one dimesion array
		voucherData.value.voucherRedeemedItems.forEach((item: any) => {
			if (item.length) {
				voucherItems.push(...item);
			} else {
				voucherItems.push(item);
			}
		});

		const transaction: Transaction = {
			items: voucherItems.map((item: any) => {
				let description = '';
				let quantity = null;
				let price = '';
				let currency = null;

					if (item.item_type === 'wallet_balance') {
						description = t('top_up.redeem_voucher.wallet_balance');
						currency = item.currency.prefix;
						price = item.amount;
					} else if (item.item_type === 'free_tickets') {
						description = `${item.item_extra_data?.event_name ?? ''} - ${item.item_extra_data?.ticket_type_name ?? ''}`;
						quantity = item.item_quantity;
						price = '0';
					} else if (item.item_type === 'tags_assignment') {
					description = `${t('top_up.redeem_voucher.now_identified_as')} ${item.item_names.join(' - ')}`;
				}

				return {
					description,
					quantity: quantity,
					itemPrice: price,
					currency,
					unitPrice: 0,
				};
			}),
			type: TransactionType.VOUCHER_TOP_UP,
			totalDescription: t('top_up.redeem_voucher.voucher_redeemed_items'),
			totalPriceString: walletBalanceAdded.length ? walletBalanceTotalString(walletBalanceAdded) : '',
			totalPriceNumber: walletBalanceTotal,
			totalPriceWithoutFee: 0,
			additionalInfo: sharedClaimSuccessMessage ? { sharedClaimSuccessMessage } : undefined,
		};
		setItemsAndTotal(transaction);
		await router.push({ path: '/transaction' });
	} else if (voucherData.value?.voucherMetaData && voucherData.value.voucherMetaData.status === 'partially_redeemed' && !voucherData.value.voucherRedeemedItems.length) {
		await presentToast('top', t('top_up.redeem_voucher.voucher_partially_redeemed'), 5000, 'danger');
	}
});

watch(errorVoucher, async () => {
	console.log(errorVoucher.value);
});

// onScanQR
const onScanQR = (qrCode: string) => {
	voucherText.value = qrCode;
};

// Go back
const goBack = () => {
	router.push({
		path: '/dashboard',
	});
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonContent), {
    style: _normalizeStyle(`background: linear-gradient(${_unref(primary_color)}, ${primaryShade});`)
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(TopBar, { class: "px-[34px]" }, {
          left: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_unref(IonButton), {
                class: "w-[35px] h-[35px] text-black ion-no-padding ion-no-margin hover:bg-white hover:rounded-full",
                shape: "round",
                onClick: goBack
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(IonIcon), {
                    icon: _unref(chevronBackOutline),
                    class: "ion-no-padding ion-no-margin text-black mr-[2px]"
                  }, null, 8, ["icon"])
                ]),
                _: 1
              })
            ])
          ]),
          center: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)('top_up.redeem_voucher.title')), 1)
          ]),
          _: 1
        }),
        (_unref(isLoadingVoucher))
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(_unref(IonSpinner), { name: "dots" })
            ]))
          : _createCommentVNode("", true),
        (!_unref(isLoadingVoucher) && qrErrorMessage.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_unref(IonIcon), {
                  icon: _unref(alertCircleOutline),
                  class: "text-[64px] mb-4"
                }, null, 8, ["icon"]),
                _createElementVNode("h1", _hoisted_7, _toDisplayString(_unref(t)('top_up.redeem_voucher.error_title')), 1),
                _createElementVNode("p", _hoisted_8, _toDisplayString(qrErrorMessage.value), 1)
              ])
            ]))
          : _createCommentVNode("", true),
        (!_unref(isLoadingVoucher) && !qrErrorMessage.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, _toDisplayString(_unref(t)('top_up.redeem_voucher.redeem_here')), 1),
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("div", _hoisted_12, [
                  _createVNode(_unref(IonInput), {
                    modelValue: _unref(voucherText),
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(voucherText) ? (voucherText).value = $event : null)),
                    class: "text-center font-sourcesanspro-regular text-l2 tracking-[.20em]",
                    name: "voucher-code",
                    type: "text",
                    placeholder: "xxxx - xxxx - xxxx"
                  }, null, 8, ["modelValue"])
                ])
              ]),
              _createElementVNode("div", _hoisted_13, [
                _createElementVNode("h5", null, [
                  _createElementVNode("span", _hoisted_14, _toDisplayString(_unref(t)('top_up.redeem_voucher.redeem_or')), 1)
                ])
              ]),
              _createVNode(ScanQR, {
                onOnScanQR: onScanQR,
                extractParam: "voucherID",
                permissionTitle: _unref(t)('scan_qr.camera_permission_title'),
                permissionDescription: _unref(t)('scan_qr.camera_permission_description')
              }, null, 8, ["permissionTitle", "permissionDescription"])
            ]))
          : _createCommentVNode("", true),
        (qrErrorMessage.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
              _createVNode(_unref(IonButton), {
                class: "w-full hover:bg-white hover:rounded-full",
                expand: "full",
                size: "large",
                shape: "round",
                onClick: goBack
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_16, [
                    _createTextVNode(_toDisplayString(_unref(t)('top_up.redeem_voucher.back_to_dashboard')) + " ", 1),
                    _createVNode(_unref(IonIcon), {
                      icon: _unref(arrowForwardOutline),
                      class: "text-dark-blue mr-2"
                    }, null, 8, ["icon"])
                  ])
                ]),
                _: 1
              })
            ]))
          : _createCommentVNode("", true),
        (!qrErrorMessage.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
              _createVNode(_unref(IonButton), {
                class: "w-full hover:bg-white hover:rounded-full",
                expand: "full",
                size: "large",
                shape: "round",
                disabled: _unref(isLoadingVoucher) || !_unref(voucherText),
                onClick: redeemVoucherManually
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_18, [
                    _createTextVNode(_toDisplayString(_unref(t)('general.continue')) + " ", 1),
                    _createVNode(_unref(IonIcon), {
                      icon: _unref(arrowForwardOutline),
                      class: "text-dark-blue mr-2"
                    }, null, 8, ["icon"])
                  ])
                ]),
                _: 1
              }, 8, ["disabled"])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["style"]))
}
}

})